import React, { useState, useEffect } from 'react';
import { X, Search, Sparkles } from 'lucide-react';
import { loadBrandFonts } from '../../utils/brandFonts';

interface FontEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (font: string, type: 'heading' | 'body' | 'buttons' | 'captions' | 'navigation', subtype?: 'h1' | 'h2' | 'h3' | 'primary' | 'secondary') => void;
  initialFont?: string;
  fontType: 'heading' | 'body' | 'buttons' | 'captions' | 'navigation';
  fontSubtype?: 'h1' | 'h2' | 'h3' | 'primary' | 'secondary';
}

const GOOGLE_FONTS = [
  { name: 'Inter', category: 'Sans-serif', styles: 18 },
  { name: 'Roboto', category: 'Sans-serif', styles: 12 },
  { name: 'Open Sans', category: 'Sans-serif', styles: 10 },
  { name: 'Lato', category: 'Sans-serif', styles: 9 },
  { name: 'Montserrat', category: 'Sans-serif', styles: 18 },
  { name: 'Source Sans Pro', category: 'Sans-serif', styles: 12 },
  { name: 'Oswald', category: 'Sans-serif', styles: 6 },
  { name: 'Raleway', category: 'Sans-serif', styles: 18 },
  { name: 'Poppins', category: 'Sans-serif', styles: 18 },
  { name: 'Nunito', category: 'Sans-serif', styles: 12 },
  { name: 'Work Sans', category: 'Sans-serif', styles: 9 },
  { name: 'Fira Sans', category: 'Sans-serif', styles: 10 },
  { name: 'PT Sans', category: 'Sans-serif', styles: 4 },
  { name: 'Ubuntu', category: 'Sans-serif', styles: 8 },
  { name: 'Noto Sans', category: 'Sans-serif', styles: 12 },
  { name: 'Bricolage Grotesque', category: 'Sans-serif', styles: 4 },
  { name: 'Albert Sans', category: 'Sans-serif', styles: 18 },
  { name: 'DM Sans', category: 'Sans-serif', styles: 11 },
  { name: 'Plus Jakarta Sans', category: 'Sans-serif', styles: 14 },
  { name: 'Outfit', category: 'Sans-serif', styles: 9 },
  { name: 'Manrope', category: 'Sans-serif', styles: 8 },
  { name: 'Space Grotesk', category: 'Sans-serif', styles: 7 },
  { name: 'Lexend', category: 'Sans-serif', styles: 9 },
  { name: 'Satoshi', category: 'Sans-serif', styles: 10 },
  { name: 'Figtree', category: 'Sans-serif', styles: 9 },
  
  { name: 'Playfair Display', category: 'Serif', styles: 9 },
  { name: 'Montagu Slab', category: 'Serif', styles: 7 },
  { name: 'Merriweather', category: 'Serif', styles: 8 },
  { name: 'Crimson Text', category: 'Serif', styles: 6 },
  { name: 'Libre Baskerville', category: 'Serif', styles: 3 },
  
  { name: 'Dancing Script', category: 'Handwriting', styles: 2 },
  { name: 'Pacifico', category: 'Handwriting', styles: 1 },
  { name: 'Great Vibes', category: 'Handwriting', styles: 1 },
  
  { name: 'Bebas Neue', category: 'Display', styles: 1 },
  { name: 'Righteous', category: 'Display', styles: 1 },
  { name: 'Fredoka One', category: 'Display', styles: 1 },
];

const FONT_CATEGORIES = ['All', 'Sans-serif', 'Serif', 'Display', 'Handwriting'];

export const FontEditModal: React.FC<FontEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialFont = '',
  fontType,
  fontSubtype
}) => {
  const [selectedFont, setSelectedFont] = useState(initialFont);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState(fontType);
  const [selectedSubtype, setSelectedSubtype] = useState(fontSubtype);

  useEffect(() => {
    if (isOpen) {
      // Every tile below previews itself in its own family, so pull the catalogue
      // in before the grid paints, not at app boot.
      loadBrandFonts();
      setSelectedFont(initialFont);
      setSelectedType(fontType);
      setSelectedSubtype(fontSubtype);
      setSearchTerm('');
      setSelectedCategory('All');
    }
  }, [isOpen, initialFont, fontType, fontSubtype]);

  if (!isOpen) return null;

  const filteredFonts = GOOGLE_FONTS.filter(font => {
    const matchesSearch = font.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || font.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSave = () => {
    if (selectedFont && selectedType) {
      onSave(selectedFont, selectedType, selectedSubtype);
    }
  };

  const getTypeDisplayName = (type: string, subtype?: string) => {
    if (type === 'heading' && subtype) return `Heading ${subtype.toUpperCase()}`;
    if (type === 'body' && subtype) return `Body ${subtype}`;
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary-500" />
              <h2 className="text-xl font-semibold">
                {initialFont ? 'Edit Font' : 'Select Font'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Font Preview */}
          {selectedFont && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="text-sm text-gray-600 mb-2">Preview:</div>
              <div 
                className="text-3xl font-bold mb-2"
                style={{ fontFamily: `"${selectedFont}", serif` }}
              >
                {selectedFont}
              </div>
              <div 
                className="text-lg text-gray-600"
                style={{ fontFamily: `"${selectedFont}", serif` }}
              >
                The quick brown fox jumps over the lazy dog
              </div>
            </div>
          )}

          {/* Font Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Font Usage
            </label>
            <div className="space-y-2">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as any)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                <option value="heading">Heading</option>
                <option value="body">Body Text</option>
                <option value="buttons">Buttons</option>
                <option value="captions">Captions</option>
                <option value="navigation">Navigation</option>
              </select>
              
              {(selectedType === 'heading' || selectedType === 'body') && (
                <select
                  value={selectedSubtype || ''}
                  onChange={(e) => setSelectedSubtype(e.target.value as any)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  {selectedType === 'heading' ? (
                    <>
                      <option value="h1">H1 (Main Headlines)</option>
                      <option value="h2">H2 (Section Headers)</option>
                      <option value="h3">H3 (Sub-headers)</option>
                    </>
                  ) : (
                    <>
                      <option value="primary">Primary Body Text</option>
                      <option value="secondary">Secondary Body Text</option>
                    </>
                  )}
                </select>
              )}
            </div>
          </div>

          {/* Search and Filter */}
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search fonts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              {FONT_CATEGORIES.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Font Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredFonts.map(font => (
              <button
                key={font.name}
                onClick={() => setSelectedFont(font.name)}
                className={`p-4 rounded-lg border-2 text-left transition-all hover:border-primary-300 ${
                  selectedFont === font.name
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div 
                  className="text-xl font-semibold mb-2"
                  style={{ fontFamily: `"${font.name}", serif` }}
                >
                  {font.name}
                </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <img 
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
                        alt="Google" 
                        className="w-3 h-3" 
                      />
                      <span>Google</span>
                    </div>
                  <span>{font.styles} styles</span>
                  <span>Free</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">{font.category}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
                      <button
              onClick={handleSave}
              disabled={!selectedFont}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedFont
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
            Save Font
          </button>
        </div>
      </div>
    </div>
  );
}; 